import React, { useState, useEffect, useCallback } from 'react';
import { getPatientTransactions } from '../../utils/apis';

interface Transaction {
  transactionId: number;
  accountId: number;
  accountNumber: string;
  transactionType: string;
  serviceDate: string;
  chargemasterCode?: string;
  altCodeType?: string;
  altCode?: string;
  codeDescription?: string;
  amount: number;
  countValue: number;
  comment?: string;
  sourceRowHash: string;
  createdAt: string;
}

interface BillSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: number;
  accountNumber: string;
  guarantorName: string;
  guarantorNumber: string;
  totalAmount: number;
}

const BillSummaryModal: React.FC<BillSummaryModalProps> = ({
  isOpen,
  onClose,
  accountId,
  accountNumber,
  guarantorName,
  guarantorNumber,
  totalAmount
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPatientTransactions(accountId);
      setTransactions(response.transactions || []);
    } catch (err) {
      setError('Failed to fetch transaction details');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (isOpen && accountId) {
      // Clear previous data before fetching new data
      setTransactions([]);
      setError(null);
      fetchTransactions();
    } else if (!isOpen) {
      // Clear data when modal closes
      setTransactions([]);
      setError(null);
    }
  }, [isOpen, accountId, fetchTransactions]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const calculateTotals = () => {
    const charges = transactions
      .filter(t => t.transactionType === 'Charge')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const payments = transactions
      .filter(t => t.transactionType === 'Payment')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const adjustments = transactions
      .filter(t => t.transactionType === 'Adjustment')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const totalPaymentsAdjustments = payments + adjustments;
    const patientBalance = charges - totalPaymentsAdjustments;

    return {
      totalCharges: charges,
      totalPayments: payments,
      totalAdjustments: adjustments,
      totalPaymentsAdjustments,
      patientBalance
    };
  };

  const totals = calculateTotals();

  const groupedTransactions = transactions.reduce((groups, transaction) => {
    const accountNum = transaction.accountNumber;
    if (!groups[accountNum]) {
      groups[accountNum] = [];
    }

    if(transaction.transactionType === 'Bad Debt Transfer') {
      return groups;
    }
    groups[accountNum].push(transaction);
    return groups;
  }, {} as Record<string, Transaction[]>);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-4">Bill Summary</h2>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span className="font-medium text-slate-600">Guarantor:</span>
                  <span className="font-semibold text-slate-800">{guarantorName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="font-medium text-slate-600">Account #:</span>
                  <span className="font-semibold text-slate-800">{guarantorNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  <span className="font-medium text-slate-600">Service Date:</span>
                  <span className="font-semibold text-slate-800">{formatDate(transactions[0]?.serviceDate || new Date().toISOString())}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  <span className="font-medium text-slate-600">Due Date:</span>
                  <span className="font-semibold text-slate-800">Upon receipt</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-3xl transition-colors duration-200 hover:bg-slate-100 rounded-full p-2"
            >
              ×
            </button>
          </div>

          {/* Combined Financial Summary and Amount Due */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-slate-800 mb-1">Total Amount Due</h3>
                <p className="text-sm text-slate-600">All charges and adjustments included</p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-bold text-blue-600">
                  {formatCurrency(totalAmount)}
                </div>
                <div className="text-sm text-slate-500 mt-1">USD</div>
              </div>
            </div>
            
            {/* Financial Summary Cards - Only show when we have transaction data */}
            {transactions.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-slate-600">Total Charges</span>
                  </div>
                  <div className="text-xl font-bold text-slate-800">{formatCurrency(totals.totalCharges)}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-slate-600">Payments/Adjustments</span>
                  </div>
                  <div className="text-xl font-bold text-green-600">-{formatCurrency(totals.totalPaymentsAdjustments)}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border-2 border-blue-300 bg-blue-50">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    <span className="text-sm font-medium text-slate-700">Final Balance</span>
                  </div>
                  <div className="text-xl font-bold text-blue-600">{formatCurrency(totals.patientBalance)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Transaction Details */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">Transaction Breakdown</h3>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Charges</span>
                <div className="w-2 h-2 bg-green-500 rounded-full ml-4"></div>
                <span>Payments/Adjustments</span>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
                <p className="mt-4 text-slate-600 font-medium">Loading transaction details...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-600 text-2xl">⚠</span>
                </div>
                <p className="text-red-600 font-medium mb-4">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedTransactions).map(([accountNum, accountTransactions]) => (
                  <div key={accountNum} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <h4 className="font-semibold text-slate-800">Account #{accountNum}</h4>
                        </div>
                        <div className="text-sm text-slate-600">
                          {guarantorName}
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Service Description</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Charges</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Payments/Adj.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {accountTransactions.map((transaction, index) => (
                            <tr key={transaction.transactionId} className={`${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'} hover:bg-indigo-50 transition-colors duration-150`}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800">
                                {formatDate(transaction.serviceDate)}
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="text-sm font-medium text-slate-800">
                                    {transaction.codeDescription || transaction.comment || 'Service'}
                                  </div>
                                  {transaction.chargemasterCode && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      {transaction.chargemasterCode} {transaction.altCode && `(${transaction.altCode})`}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                {transaction.transactionType === 'Charge' ? (
                                  <span className="font-semibold text-slate-800">{formatCurrency(transaction.amount)}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                {(transaction.transactionType === 'Payment' || transaction.transactionType === 'Adjustment') ? (
                                  <span className="font-semibold text-green-600">-{formatCurrency(Math.abs(transaction.amount))}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 border-t border-slate-200">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors duration-200 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillSummaryModal;
